import React, { useEffect, useState, useRef } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { CircularProgress } from "@mui/material";
import { useShowAlert } from "../../../../../context/AlertContext";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import InterRegular from "../../../sale/font/Inter_24pt-Regular-normal";
import InterBold from "../../../sale/font/Inter_24pt-Bold-normal";
import { useNetwork } from "../../../../../context/NetworkContext";
import { usePermission } from "../../../../../context/PermissionContext";
import AxiosInstance from "../../../../../utils/AxiosInstance";
import uRLs from "../../../../../utils/baseUtils";
import handleError from "../../../../../constants/handleError";
import { useAuth } from "../../../../../context/AuthProvider";
import CashButton from "../../../sale/cashbtn";
import PayLaterButton from "../../../sale/PaylaterBtn";
import { FormatedSaleData } from "../../../../../utils/print/GlobelSaleData";
import { DownloadIcon, PrinterIcon, ShowIcon } from "../../../../../constants/Icons";

const InvoiceThumnail2 = ({
  isViewData,
  selectedTheme,
  selectedSize,
  downloadbtn = false,
  payment=false,
}) => {
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { storeSettings } = usePermission();
  const [download, setDownload] = useState(false);
  const [print, setPrint] = useState(false);
  const cashRef = useRef();
  const paylaterRef = useRef();

  const [terms, setTerms] = useState([]);
  const [data, setData] = useState({});
  const globalData = FormatedSaleData(data);
  const { settings } = useAuth();
  const sizeOptions = [
    { key: "BILL", label: "BILL" },
    { key: "INVOICE", label: "INVOICE" },
  ];

  useEffect(() => {
    setTerms(storeSettings?.invoiceSettings?.termsAndConditions);
  }, [storeSettings?.invoiceSettings]);

  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECIEPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const formatCurrency = (value) => {
    const formatted = CurrencyConverter.toCurrency(Math.abs(value), "INR");
    return value < 0 ? `-₹${formatted}` : `₹${formatted}`;
  };

  const saleDate = data?.saledate;
  const timezone = data?.saleby?.timezone;

  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  let createdTime = "--:--";
  if (data?.createdAt) {
    const zoned = DateConverter.convertToTimeZone(data.createdAt, timezone);
    createdTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  const handleSaleItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/sale/${saleId}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const loadImageDimensions = (base64Src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = base64Src;
    });
  };

  const generateBillingPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const padding = 7;
    const headerX = 2;
    const headerY = 2;
    const imagePath = settings?.saleProfileData?.image
      ? uRLs?.IMAGE_URL + "profile/" + settings?.saleProfileData?.image
      : "/assets/images/logo-light.png";

    if (settings?.saleProfileData?.image || "/assets/images/logo-light.png") {
      try {
        // Convert image URL to base64
        const response = await fetch(imagePath);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Load original dimensions
        const { width: naturalWidth, height: naturalHeight } =
          await loadImageDimensions(base64);

        // Resize logic
        const maxWidth = 50;
        const maxHeight = 10;
        let imgWidth = naturalWidth;
        let imgHeight = naturalHeight;
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        imgWidth *= ratio;
        imgHeight *= ratio;

        const getImageFormat = (path) => {
          const ext = path.split(".").pop().toLowerCase();
          if (ext === "jpg" || ext === "jpeg") return "JPEG";
          if (ext === "png") return "PNG";
          return "JPEG";
        };
        const format = getImageFormat(imagePath);

        doc.addImage(
          base64,
          format,
          headerX + padding,
          headerY + padding,
          imgWidth,
          imgHeight,
        );
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    const addressX = headerX + padding + 95; // right of logo
    let addressY = headerY + 13; // top align with image

    // --- Company Title (dark gray & right-aligned) ---
    doc.setFontSize(8);
    doc.setTextColor(71, 69, 67); // dark gray
    doc.setFont("Inter-Bold", "bold");

    const titleText = String(settings?.saleProfileData?.title || "");
    const maxTitleWidth = pageWidth - (addressX - headerX) - 10; // available width
    const titleWidth = doc.getTextWidth(titleText);

    // Right-align title within the available space
    doc.text(titleText, addressX + maxTitleWidth - titleWidth, addressY);
    addressY += 3;

    // --- Address Line 1 ---
    doc.setFontSize(7);
    doc.setFont("Inter-Regular", "normal");
    // black for address

    if (settings?.saleProfileData?.addressline1) {
      const lineWidth1 = doc.getTextWidth(
        settings?.saleProfileData?.addressline1,
      );
      doc.text(
        settings?.saleProfileData?.addressline1,
        addressX + maxTitleWidth - lineWidth1,
        addressY,
      );
      addressY += 3;
    }

    // --- Address Line 2 ---
    if (settings?.saleProfileData?.addressline2) {
      const lineWidth2 = doc.getTextWidth(
        settings?.saleProfileData?.addressline2,
      );
      doc.text(
        settings?.saleProfileData?.addressline2,
        addressX + maxTitleWidth - lineWidth2,
        addressY,
      );
      addressY += 3;
    }

    // --- District, State, Country, Pincode + Phone in single line ---
    let combinedLine = [
      settings?.saleProfileData?.district,
      settings?.saleProfileData?.state,
      settings?.saleProfileData?.country,
    ]
      .filter(Boolean)
      .join(", ");

    if (settings?.saleProfileData?.pincode) {
      combinedLine += ` - ${settings?.saleProfileData?.pincode}`;
    }

    // Append phone if exists
    if (settings?.saleProfileData?.phone) {
      combinedLine += combinedLine
        ? ` | Ph: ${settings?.saleProfileData?.phone}`
        : `Ph: ${settings?.saleProfileData?.phone}`;
    }

    // Right-align combined line
    if (combinedLine) {
      const combinedLineWidth = doc.getTextWidth(combinedLine);
      doc.text(
        combinedLine,
        addressX + maxTitleWidth - combinedLineWidth,
        addressY,
      );
      addressY += 3;
    }

    let emailCINLine = "";
    if (settings?.saleProfileData?.email) {
      emailCINLine += settings?.saleProfileData?.email;
    }
    if (settings?.saleProfileData?.cinnumber) {
      emailCINLine += emailCINLine
        ? ` | CIN: ${settings?.saleProfileData?.cinnumber}`
        : `CIN: ${settings?.saleProfileData?.cinnumber}`;
    }

    if (emailCINLine) {
      const emailCINWidth = doc.getTextWidth(emailCINLine);
      doc.text(
        emailCINLine,
        addressX + maxTitleWidth - emailCINWidth,
        addressY,
      );
      addressY += 3;
    }

    let GSTFSSAILine = "";
    if (settings?.saleProfileData?.gstnumber) {
      GSTFSSAILine += settings?.saleProfileData?.gstnumber;
    }
    if (settings?.saleProfileData?.fssai) {
      GSTFSSAILine += GSTFSSAILine
        ? ` | FSSAI: ${settings?.saleProfileData?.fssai}`
        : `FSSAI: ${settings?.saleProfileData?.fssai}`;
    }

    if (GSTFSSAILine) {
      const emailCINWidth = doc.getTextWidth(GSTFSSAILine);
      doc.text(
        GSTFSSAILine,
        addressX + maxTitleWidth - emailCINWidth,
        addressY,
      );
      addressY += 3;
    }
    let State = "";
    if (settings?.saleProfileData?.gstnumber) {
      State += settings?.saleProfileData?.gstnumber;
    }
    if (settings?.saleProfileData?.fssai) {
      State += State
        ? ` | State: ${settings?.saleProfileData?.state}`
        : `State: ${settings?.saleProfileData?.state} [State Code:${settings?.saleProfileData?.statecode}]`;
    }

    if (State) {
      const emailCINWidth = doc.getTextWidth(State);
      doc.text(State, addressX + maxTitleWidth - emailCINWidth, addressY);
      addressY += 3;
    }
    const invoiceFontSize = 16;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(invoiceFontSize);
    const textHeightOffset = invoiceFontSize * 0.55;
    const invoiceTextY = headerY + textHeightOffset;
    const invoiceX = pageWidth - padding;
    const invoiceText = selectedSize && formatTrackMode(selectedSize);
    const invoiceTextWidth = doc.getTextWidth(invoiceText);
    doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });

    doc.setFontSize(8);
    const invoiceNoLabel = "Bill No:";
    const invoiceNoValue = data.saleno;
    const invoiceDateLabel = "Date:";
    const invoiceDateValue = DateConverter.ISOToDisplayFormat(
      data.saledate,
    ).date;
    // Measure total widths
    const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
    const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
    const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
    const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
    const gap = 7;
    const innerGap = 2;
    const invoiceRowY = 39;
    const totalTextWidth =
      invoiceNoLabelWidth +
      innerGap +
      invoiceNoValueWidth +
      gap +
      invoiceDateLabelWidth +
      innerGap +
      invoiceDateValueWidth;
    const invoiceRowX = pageWidth - padding - totalTextWidth;
    const lineStartX = padding;
    const lineEndX = invoiceRowX - 5;
    const lineY = invoiceRowY;
    // Middle line
    doc.setDrawColor(selectedTheme.templateTheme?.primaryColor);
    doc.setLineWidth(2);
    doc.setLineCap("round");
    doc.line(lineStartX, lineY, lineEndX, lineY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceNoValue,
      invoiceRowX + invoiceNoLabelWidth + innerGap,
      invoiceRowY,
    );
    const dateX =
      invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;
    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceDateLabel, dateX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceDateValue,
      dateX + invoiceDateLabelWidth + innerGap,
      invoiceRowY,
    );
    const sectionTop = 47;
    const midX = pageWidth / 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    const hasSaleToData =
      data.saleto &&
      (data.saleto.name ||
        data.saleto.addressline1 ||
        data.saleto.addressline2 ||
        data.saleto.district ||
        data.saleto.country ||
        data.saleto.pincode ||
        data.saleto.phone ||
        data.saleto.email ||
        data.saleto.gstnumber ||
        data.saleto.state ||
        data.saleto.statecode ||
        data.saleto.referid);
    if (hasSaleToData) {
      doc.text("Bill To:", padding, sectionTop);
      doc.text("Shipping Address:", midX, sectionTop);
    }

    doc.setFontSize(8);
    let fromY = sectionTop + 5;
    let toY = sectionTop + 5;
    let fromDetails = [];
    if (hasSaleToData) {
      fromDetails = [
        { bold: true, text: data.saleto?.name || "-" },
        {
          bold: false,
          text: [
            data.saleto?.addressline1,
            data?.saleto?.state,
            data.saleto?.country,
            data.saleto?.pincode ? `- ${data.saleto.pincode}` : "",
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...(data.saleto?.phone
          ? [{ bold: false, text: `Phone: ${data.saleto?.phone}` }]
          : []),
        ...(data.saleto?.email
          ? [{ bold: false, text: `Email: ${data.saleto?.email}` }]
          : []),
        ...(data.saleto?.gstnumber
          ? [
              {
                bold: false,
                text: `GST:`,
                suffix: ` ${data.saleto?.gstnumber}`,
              },
            ]
          : []),
        ...(data.saleto?.state
          ? [
              {
                bold: false,
                text: `State:`,
                suffix: ` ${data.saleto?.state} [ State Code: ${
                  data.saleto?.statecode || ""
                } ]`,
              },
            ]
          : []),
        ...(data.saleto?.referid
          ? [
              {
                bold: false,
                text: "Reference No:",
                suffix: ` ${data.saleto.referid}`,
              },
            ]
          : []),
      ];
    }
    const sectionGap = 12;
    const sectionWidth = (pageWidth - padding * 2 - sectionGap) / 2 + 20;
    const fromAddressWrapWidth = sectionWidth - 30;
    const rawAddress = [
      settings?.saleProfileData?.addressline1,
      settings?.saleProfileData?.addressline2,
      settings?.saleProfileData?.district,
      settings?.saleProfileData?.country,
      settings?.saleProfileData?.pincode ? `- ${data.salefrom.pincode}` : "",
    ]
      .filter(Boolean)
      .join(", ");

    // Wrap address to sectionWidth (same used in renderDetails)
    let wrappedAddress = [];

    // Insert wrapped address lines after the title (index 1)
    wrappedAddress.forEach((line, i) => {
      fromDetails.splice(1 + i, 0, { bold: false, text: line });
    });

    let toDetails = [];
    if (hasSaleToData) {
      toDetails = [
        { bold: true, text: data.saleto?.name || "-" },
        {
          bold: false,
          text: [
            data.saleto?.addressline1,
            // data.saleto?.addressline2,
            data.saleto?.district,
            data.saleto?.country,
            data.saleto?.pincode ? `- ${data.saleto.pincode}` : "",
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...(data.saleto?.phone
          ? [{ bold: false, text: `Phone: ${data.saleto?.phone}` }]
          : []),
        ...(data.saleto?.email
          ? [{ bold: false, text: `Email: ${data.saleto?.email}` }]
          : []),
      ];
    }
    const wrapText = (text, maxWidth) => doc.splitTextToSize(text, maxWidth);
    const lineHeight = 4;
    const renderDetails = (details, startX, startY) => {
      let y = startY - 2;
      details.forEach(({ bold, text, suffix = "" }) => {
        const fullText = text + suffix;
        const lines = wrapText(fullText, sectionWidth);

        lines.forEach((line, index) => {
          if (index === 0 && suffix && bold) {
            const labelWidth = doc.getTextWidth(text);
            doc.setFont("Inter-Bold", "bold");
            doc.text(text, startX, y);
            doc.setFont("Inter-Regular", "normal");
            doc.text(suffix, startX + labelWidth, y);
          } else {
            doc.setFont(
              bold ? "Inter-Bold" : "Inter-Regular",
              bold ? "bold" : "normal",
            );
            doc.text(line, startX, y);
          }
          y += lineHeight;
        });
      });

      return y;
    };
    if (hasSaleToData) {
      fromY = renderDetails(fromDetails, padding, fromY);
    }
    toY = renderDetails(toDetails, midX, toY);

    // 3. Product Table
    const productBody = data?.saleitems?.map((item, i) => {
      const qty = item.quantity || 1;
      const rate = CurrencyConverter.toCurrency(item.soldprice || 0, "INR");
      const total = CurrencyConverter.toCurrency(
        item.lineTotalSoldPrice || 0,
        "INR",
      );
      const details = [
        item.hsnsac ? `HSN/SAC: ${item.hsnsac}` : "",
        item.discount > 0
          ? `MRP: ₹${CurrencyConverter.toCurrency(
              item.mrp || 0,
              "INR",
            )} Discount: ₹${CurrencyConverter.toCurrency(item.discount, "INR")}`
          : "",
        item.taxpercentage > 0
          ? `Tax: ₹${CurrencyConverter.toCurrency(item.baseprice, "INR")} @${
              item.taxpercentage
            }% = ₹${CurrencyConverter.toCurrency(item.taxamount || 0, "INR")}`
          : "",
      ]
        .filter(Boolean) // removes empty values
        .join(" "); // separator without commas

      const desc = [
        `${item.title}`,
        [
          item.hsnsac ? `HSN/SAC: ${item.hsnsac}` : null,
          item.discount > 0
            ? `MRP: ₹${CurrencyConverter.toCurrency(
                item.mrp || 0,
                "INR",
              )}  Discount:₹${CurrencyConverter.toCurrency(
                item.discount,
                "INR",
              )}`
            : null,
          item.taxpercentage > 0
            ? `Tax:₹${CurrencyConverter.toCurrency(item.baseprice, "INR")} @${
                item.taxpercentage
              }% = ₹${CurrencyConverter.toCurrency(item.taxamount || 0, "INR")}`
            : null,
        ],
      ].join("\n");

      return [i + 1, `${item.title}`, `₹${rate}`, qty, `₹${total}`];
    });
    // Add total row INSIDE the same table
    productBody.push([
      {
        content: "",
        colSpan: 2,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: "Total",
        styles: { halign: "center", fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: data.totalqty,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `₹${CurrencyConverter.toCurrency(
          data.grandtotal || 0,
          "INR",
        )}`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
    ]);
    autoTable(doc, {
      startY: Math.max(fromY, toY),
      head: [["S.No", "Items", "Rate", "Qty", "Total"]],
      body: productBody,
      margin: { left: 6, right: 5 }, // add right margin if needed
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 7,
        halign: "middle",
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#041121",
      },
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme.templateTheme?.foregroundColor,
        fontStyle: "bold",
        font: "Inter-Bold",
        halign: "center",
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center", valign: "middle" },
        1: { cellWidth: 131, halign: "left" },
        2: { cellWidth: 21, halign: "center", valign: "middle" },
        3: { cellWidth: 13, halign: "center", valign: "middle" },
        4: { cellWidth: 22, halign: "center", valign: "middle" },
      },
      willDrawCell: function (data) {
        if (data.column.index === 1 && data.section === "body") {
          const itemIndex = data.row.index;
          const item = data?.saleitems?.[itemIndex];
          if (!item) return;

          const x = data.cell.x + 2;
          let y = data.cell.y + 4;
          const lineHeight = 4;
          const currency = CurrencyConverter;

          const lines = [
            { type: "title", text: item.title },
            ...(item.imode === "PRODUCT" && item.note
              ? [{ type: "description", text: item.note }]
              : []),
            ...(item.imode === "SERVICE" && item.description
              ? [{ type: "description", text: item.description }]
              : []),
            ...(item.hasnsac
              ? [
                  {
                    label: item.imode === "SERVICE" ? "SAC:" : "HSN:",
                    value: item.hasnsac,
                  },
                ]
              : []),
            {
              label: "MRP:",
              value: `₹${currency.toCurrency(item.mrp || 0, "INR")}`,
            },
            {
              label: "Discount:",
              value: `₹${currency.toCurrency(item.discount || 0, "INR")}`,
            },
          ];

          data.cell.text = [];
          lines.forEach((line) => {
            if (line.type === "title") {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(15);
              doc.setTextColor(0, 0, 0);
              doc.text(line.text, x, y);
              doc.setFontSize(9);
            } else {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(9);
              doc.setTextColor(0, 0, 0);
              doc.text(line.label, x, y);
              doc.setFont("Inter-Regular", "normal");
              doc.setTextColor(80, 80, 80);
              doc.text(line.value, x + 32, y);
            }
            y += lineHeight;
          });
        }
      },

      theme: "grid",
    });
    const isInterState =
      settings?.saleProfileData?.statecode &&
      data.saleto?.statecode &&
      data.salefrom.statecode !== data.saleto.statecode;
    const body = [
      [
        {
          content: "Total Amount In Words",
          styles: {
            fontStyle: "bold",
            font: "Inter-Bold",
            fontSize: 9,
            valign: "top",
          },
        },
        {
          content: "Grand Total",
          styles: { halign: "right", fontSize: 9 },
        },
        {
          content: `₹${CurrencyConverter.toCurrency(
            data.grandtotal || 0,
            "INR",
          )}`,
          styles: { halign: "center", valign: "middle", fontSize: 9 },
        },
      ],
      [
        {
          content: data.amountinwords,
          rowSpan: isInterState ? 3 : 4,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: 9,
          },
        },
        {
          content: "Grand Total (Rounded)",
          styles: { fontStyle: "bold", halign: "right", fontSize: 9 },
        },
        {
          content: `₹ ${CurrencyConverter.toCurrency(
            data.grandtotal || 0,
            "INR",
          )}`,
          styles: {
            halign: "center",
            valign: "middle",
            fontStyle: "bold",
            fontSize: 10,
          },
        },
      ],
    ];
    const afterProductsY = doc.lastAutoTable?.finalY + 0.4;
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginBottom = 20;
    const estimatedSummaryHeight = 25;
    let summaryStartY;
    let isSamePage = true;
    if (afterProductsY + estimatedSummaryHeight + marginBottom <= pageHeight) {
      summaryStartY = afterProductsY;
    } else {
      doc.addPage();
      summaryStartY = 20;
      isSamePage = false;
    }
    autoTable(doc, {
      startY: summaryStartY,
      body: body,
      pageBreak: "avoid",
      margin: { left: 6, right: 5 },
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        halign: "middle",
        cellPadding: 2,
        fontSize: 9,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme.templateTheme?.foregroundColor,
      },
      columnStyles: {
        0: { cellWidth: 130 },
        1: { cellWidth: 45, halign: "right" },
        2: { cellWidth: 22, halign: "center", valign: "middle" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && isSamePage) {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
        const lastRowIndex = body.length - 1;
        if (data.section === "body") {
          if (
            data.row.index === lastRowIndex &&
            (data.column.index === 1 || data.column.index === 2)
          ) {
            // data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.font = "Inter-Bold";
            data.cell.styles.fontStyle = "bold";
          } else {
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fillColor = "#ffffff";
            data.cell.styles.font = "Inter-Regular";
            data.cell.styles.fontStyle = "normal";
          }
        }
      },
      theme: "grid",
    });
    const afterSummaryY = doc.lastAutoTable.finalY;
    const pageHeight2 = doc.internal.pageSize.getHeight();
    const marginBottom2 = 20;
    const declarationHeight = 45;
    let finalY;
    if (afterSummaryY + declarationHeight + marginBottom2 > pageHeight2) {
      doc.addPage();
      finalY = 10;
    } else {
      finalY = afterSummaryY;
    }
    let currentY = finalY + 10;

    if (parseFloat(data?.totaldiscount) > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text(
        `Your Total Discount : ₹${CurrencyConverter.toCurrency(
          data?.totaldiscount || 0,
          "INR"
        )}`,
        105,
        currentY,
        { align: "center" }
      );
    
      currentY += 2; // ONLY if discount exists
    }
    const paymentRows = [];
    
    const cashPaid =
      (data?.cashamount || 0) - (data?.cashchange || 0);
    
    const cardPaid =
      (data?.cardamount || 0) - (data?.cardchange || 0);
    
    const onlinePaid =
      (data?.onlineamount || 0) - (data?.onlinechange || 0);
    
    const upiPaid =
      (data?.upiamount || 0) - (data?.upichange || 0);
    
    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);
      
    // condition based rows
    if (cashPaid > 0) {
      paymentRows.push(["Cash", `₹${CurrencyConverter.toCurrency(cashPaid, "INR")}`]);
    }
    
    if (cardPaid > 0) {
      paymentRows.push(["Card", `₹${CurrencyConverter.toCurrency(cardPaid, "INR")}`]);
    }
    
    if (onlinePaid > 0) {
      paymentRows.push(["Online Amount", `₹${CurrencyConverter.toCurrency(onlinePaid, "INR")}`]);
    }
    
    if (upiPaid > 0) {
      paymentRows.push(["UPI", `₹${CurrencyConverter.toCurrency(upiPaid, "INR")}`]);
    }
    
    if (balanceDue > 0) {
      paymentRows.push([
        "Balance Due",
        `₹${CurrencyConverter.toCurrency(balanceDue, "INR")}`,
      ]);
    }
    
    // draw table ONLY if rows exist
    if (paymentRows?.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [["Payment Mode", ""]],
        body: paymentRows,
        margin: { left: 6 },
        tableWidth: 70,
        styles: {
          font: "Inter-Regular",
          fontSize: 8,
          cellPadding: 2,
          lineColor: selectedTheme?.templateTheme?.borderColor,
          lineWidth: 0.2,
          textColor: "#000",
        },
        headStyles: {
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          textColor: selectedTheme?.templateTheme?.foregroundColor,
          font: "Inter-Bold",
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: 30, halign: "right" },
        },
        theme: "grid",
      });
    
      currentY = doc.lastAutoTable.finalY + 6;
    }
    
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(
      "***** Thank You! Visit Us Again *****",
      pageWidth / 2,
      currentY,
      { align: "center" }
    );
    currentY += 5;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(
      "Powerd By",
      pageWidth / 2,
      currentY,
      { align: "center" }
    );
    if(storeSettings?.negobillBranding){
    currentY += 5;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(
      "www.NegoBill.com",
      pageWidth / 2,
      currentY,
      { align: "center" }
    );
    }
    currentY += 8;

    const contentWidth = pageWidth - padding * 2;

    // 70% for Terms
    const termsWidth = contentWidth * 0.7;

    // 30% for Signature
    const signWidth = contentWidth * 0.3;

    const leftX = padding;

    const startY = currentY;
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Terms & Conditions", leftX, startY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      const splitTerms = doc.splitTextToSize(terms, termsWidth - 5);
      doc.text(splitTerms, leftX, startY + 4);
    }

    const boxTopY = currentY + 3;
    // const boxTopY = footerY + 7;
    const boxHeight = 20;
    const boxLeftX = padding;
    const boxRightX = pageWidth - padding;
    const boxWidth = boxRightX - boxLeftX;
    const boxMidX = boxLeftX + boxWidth / 2;

    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const titleWidths = doc.getTextWidth("Issued By,Signature");
    doc.text(
      "Issued By,Signature",
      addressX + maxTitleWidth - titleWidths,
      boxTopY,
    );
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.text(titleText, addressX + maxTitleWidth - titleWidth - 6, boxTopY + 5);
    const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;
    if (storeSettings?.invoiceSettings?.signature) {
      try {
        // Convert image URL to base64
        const response = await fetch(signimg);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Load original dimensions
        const { width: naturalWidth, height: naturalHeight } =
          await loadImageDimensions(base64);

        // Resize logic
        const maxWidth = 50;
        const maxHeight = 10;
        let imgWidth = naturalWidth;
        let imgHeight = naturalHeight;
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        imgWidth *= ratio;
        imgHeight *= ratio;

        const getImageFormat = (path) => {
          const ext = path.split(".").pop().toLowerCase();
          if (ext === "jpg" || ext === "jpeg") return "JPEG";
          if (ext === "png") return "PNG";
          return "JPEG";
        };
        const format = getImageFormat(signimg);

        doc.addImage(
          base64,
          format,
          addressX + maxTitleWidth - titleWidths - 15,
          boxTopY + 7,
          imgWidth,
          imgHeight,
        );
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    setDownload(false);
    setPrint(false);
    return doc;
  };

  const generateinvoicePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    const padding = 7;
    const headerX = 2;
    const headerY = 2;
    const imagePath = settings?.saleProfileData?.image
      ? uRLs?.IMAGE_URL + "profile/" + settings?.saleProfileData?.image
      : "/assets/images/logo-light.png";

    if (settings?.saleProfileData?.image || "/assets/images/logo-light.png") {
      try {
        // Convert image URL to base64
        const response = await fetch(imagePath);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Load original dimensions
        const { width: naturalWidth, height: naturalHeight } =
          await loadImageDimensions(base64);

        // Resize logic
        const maxWidth = 50;
        const maxHeight = 10;
        let imgWidth = naturalWidth;
        let imgHeight = naturalHeight;
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        imgWidth *= ratio;
        imgHeight *= ratio;

        const getImageFormat = (path) => {
          const ext = path.split(".").pop().toLowerCase();
          if (ext === "jpg" || ext === "jpeg") return "JPEG";
          if (ext === "png") return "PNG";
          return "JPEG";
        };
        const format = getImageFormat(imagePath);

        doc.addImage(
          base64,
          format,
          headerX + padding,
          headerY + padding,
          imgWidth,
          imgHeight,
        );
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    const addressX = headerX + padding + 95; // right of logo
    let addressY = headerY + 13; // top align with image

    // --- Company Title (dark gray & right-aligned) ---
    doc.setFontSize(8);
    doc.setTextColor(71, 69, 67); // dark gray
    doc.setFont("Inter-Bold", "bold");

    const titleText = String(settings?.saleProfileData?.title || "");
    const maxTitleWidth = pageWidth - (addressX - headerX) - 10; // available width
    const titleWidth = doc.getTextWidth(titleText);

    // Right-align title within the available space
    doc.text(titleText, addressX + maxTitleWidth - titleWidth, addressY);
    addressY += 3;

    // --- Address Line 1 ---
    doc.setFontSize(7);
    doc.setFont("Inter-Regular", "normal");
    // black for address

    if (settings?.saleProfileData?.addressline1) {
      const lineWidth1 = doc.getTextWidth(
        settings?.saleProfileData?.addressline1,
      );
      doc.text(
        settings?.saleProfileData?.addressline1,
        addressX + maxTitleWidth - lineWidth1,
        addressY,
      );
      addressY += 3;
    }

    // --- Address Line 2 ---
    if (settings?.saleProfileData?.addressline2) {
      const lineWidth2 = doc.getTextWidth(
        settings?.saleProfileData?.addressline2,
      );
      doc.text(
        settings?.saleProfileData?.addressline2,
        addressX + maxTitleWidth - lineWidth2,
        addressY,
      );
      addressY += 3;
    }

    // --- District, State, Country, Pincode + Phone in single line ---
    let combinedLine = [
      settings?.saleProfileData?.district,
      settings?.saleProfileData?.state,
      settings?.saleProfileData?.country,
    ]
      .filter(Boolean)
      .join(", ");

    if (settings?.saleProfileData?.pincode) {
      combinedLine += ` - ${settings?.saleProfileData?.pincode}`;
    }

    // Append phone if exists
    if (settings?.saleProfileData?.phone) {
      combinedLine += combinedLine
        ? ` | Ph: ${settings?.saleProfileData?.phone}`
        : `Ph: ${settings?.saleProfileData?.phone}`;
    }

    // Right-align combined line
    if (combinedLine) {
      const combinedLineWidth = doc.getTextWidth(combinedLine);
      doc.text(
        combinedLine,
        addressX + maxTitleWidth - combinedLineWidth,
        addressY,
      );
      addressY += 3;
    }

    let emailCINLine = "";
    if (settings?.saleProfileData?.email) {
      emailCINLine += settings?.saleProfileData?.email;
    }
    if (settings?.saleProfileData?.cinnumber) {
      emailCINLine += emailCINLine
        ? ` | CIN: ${settings?.saleProfileData?.cinnumber}`
        : `CIN: ${settings?.saleProfileData?.cinnumber}`;
    }

    if (emailCINLine) {
      const emailCINWidth = doc.getTextWidth(emailCINLine);
      doc.text(
        emailCINLine,
        addressX + maxTitleWidth - emailCINWidth,
        addressY,
      );
      addressY += 3;
    }

    let GSTFSSAILine = "";
    if (settings?.saleProfileData?.gstnumber) {
      GSTFSSAILine += settings?.saleProfileData?.gstnumber;
    }
    if (settings?.saleProfileData?.fssai) {
      GSTFSSAILine += GSTFSSAILine
        ? ` | FSSAI: ${settings?.saleProfileData?.fssai}`
        : `FSSAI: ${settings?.saleProfileData?.fssai}`;
    }

    if (GSTFSSAILine) {
      const emailCINWidth = doc.getTextWidth(GSTFSSAILine);
      doc.text(
        GSTFSSAILine,
        addressX + maxTitleWidth - emailCINWidth,
        addressY,
      );
      addressY += 3;
    }
    let State = "";
    if (settings?.saleProfileData?.gstnumber) {
      State += settings?.saleProfileData?.gstnumber;
    }
    if (settings?.saleProfileData?.fssai) {
      State += State
        ? ` | State: ${settings?.saleProfileData?.state}`
        : `State: ${settings?.saleProfileData?.state} [State Code:${settings?.saleProfileData?.statecode}]`;
    }

    if (State) {
      const emailCINWidth = doc.getTextWidth(State);
      doc.text(State, addressX + maxTitleWidth - emailCINWidth, addressY);
      addressY += 3;
    }
    const invoiceFontSize = 16;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(invoiceFontSize);
    const textHeightOffset = invoiceFontSize * 0.55;
    const invoiceTextY = headerY + textHeightOffset;
    const invoiceX = pageWidth - padding;
    const invoiceText = selectedSize && formatTrackMode(selectedSize);
    const invoiceTextWidth = doc.getTextWidth(invoiceText);
    doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });

    doc.setFontSize(8);
    const invoiceNoLabel = "Invoice No:";
    const invoiceNoValue = globalData.saleno;
    const invoiceDateLabel = "Date:";
    const invoiceDateValue = DateConverter.ISOToDisplayFormat(
      globalData.saledate,
    ).date;
    // Measure total widths
    const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
    const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
    const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
    const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
    const gap = 7;
    const innerGap = 2;
    const invoiceRowY = 39;
    const totalTextWidth =
      invoiceNoLabelWidth +
      innerGap +
      invoiceNoValueWidth +
      gap +
      invoiceDateLabelWidth +
      innerGap +
      invoiceDateValueWidth;
    const invoiceRowX = pageWidth - padding - totalTextWidth;
    const lineStartX = padding;
    const lineEndX = invoiceRowX - 5;
    const lineY = invoiceRowY;
    // Middle line
    doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
    doc.setLineWidth(2);
    doc.setLineCap("round");
    doc.line(lineStartX, lineY, lineEndX, lineY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceNoValue,
      invoiceRowX + invoiceNoLabelWidth + innerGap,
      invoiceRowY,
    );
    const dateX =
      invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;
    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceDateLabel, dateX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceDateValue,
      dateX + invoiceDateLabelWidth + innerGap,
      invoiceRowY,
    );
    const sectionTop = 47;
    const midX = pageWidth / 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    const hasSaleToData =
      globalData.saleto &&
      (globalData.saleto.name ||
        globalData.saleto.addressline1 ||
        globalData.saleto.addressline2 ||
        globalData.saleto.district ||
        globalData.saleto.country ||
        globalData.saleto.pincode ||
        globalData.saleto.phone ||
        globalData.saleto.email ||
        globalData.saleto.gstnumber ||
        globalData.saleto.state ||
        globalData.saleto.statecode ||
        globalData.saleto.referid);
        const hasShippingAddress =
        globalData.saleto?.shippingAddress &&
        (globalData?.saleto?.shippingAddress?.addressline1 ||
        globalData?.saleto?.shippingAddress?.addressline2 );

    if (hasSaleToData) {
      doc.text("Invoice To:", padding, sectionTop);
      // doc.text("Shipping Address:", midX, sectionTop);
    }
   if (hasShippingAddress) {
  doc.text("Shipping Address:", midX, sectionTop);
}

    doc.setFontSize(8);
    let fromY = sectionTop + 5;
    let toY = sectionTop + 5;
    let fromDetails = [];
    if (hasSaleToData) {
      fromDetails = [
        { bold: true, text: globalData.saleto?.name || "-" },
        {
          bold: false,
          text: [
            globalData.saleto?.addressline1,
            globalData?.saleto?.state,
            globalData.saleto?.country,
            globalData.saleto?.pincode ? `- ${globalData.saleto.pincode}` : "",
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...(globalData.saleto?.phone
          ? [{ bold: false, text: `Phone: ${globalData.saleto?.phone}` }]
          : []),
        ...(globalData.saleto?.email
          ? [{ bold: false, text: `Email: ${globalData.saleto?.email}` }]
          : []),
        ...(globalData.saleto?.gstnumber
          ? [
              {
                bold: false,
                text: `GST:`,
                suffix: ` ${globalData.saleto?.gstnumber}`,
              },
            ]
          : []),
        ...(globalData.saleto?.state
          ? [
              {
                bold: false,
                text: `State:`,
                suffix: ` ${globalData.saleto?.state} [ State Code: ${
                  globalData.saleto?.statecode || ""
                } ]`,
              },
            ]
          : []),
        ...(globalData.saleto?.referid
          ? [
              {
                bold: false,
                text: "Reference No:",
                suffix: ` ${globalData.saleto.referid}`,
              },
            ]
          : []),
      ];
    }
    const sectionGap = 12;
    const sectionWidth = (pageWidth - padding * 2 - sectionGap) / 2 + 20;
    const fromAddressWrapWidth = sectionWidth - 30;
    const rawAddress = [
      settings?.saleProfileData?.addressline1,
      settings?.saleProfileData?.addressline2,
      settings?.saleProfileData?.district,
      settings?.saleProfileData?.country,
      settings?.saleProfileData?.pincode ? `- ${globalData.salefrom.pincode}` : "",
    ]
      .filter(Boolean)
      .join(", ");

    // Wrap address to sectionWidth (same used in renderDetails)
    let wrappedAddress = [];

    // Insert wrapped address lines after the title (index 1)
    wrappedAddress.forEach((line, i) => {
      fromDetails.splice(1 + i, 0, { bold: false, text: line });
    });

    let toDetails = [];
    if (hasSaleToData) {
     const shipping = globalData?.saleto?.shippingAddress;

toDetails = [
  { bold: true, text: globalData.saleto?.name || "-" },
  {
    bold: false,
    text: [
      shipping?.addressline1,
      shipping?.addressline2,
      shipping?.district,
      shipping?.state,
      shipping?.country,
      shipping?.pincode ? `- ${shipping.pincode}` : "",
    ]
      .filter(Boolean)
      .join(", "),
  },
  ...(shipping?.phone
    ? [{ bold: false, text: `Phone: ${shipping.phone}` }]
    : []),
  ...(shipping?.email
    ? [{ bold: false, text: `Email: ${shipping.email}` }]
    : []),
];
    }
    const wrapText = (text, maxWidth) => doc.splitTextToSize(text, maxWidth);
    const lineHeight = 4;
    const renderDetails = (details, startX, startY) => {
      let y = startY - 2;
      details.forEach(({ bold, text, suffix = "" }) => {
        const fullText = text + suffix;
        const lines = wrapText(fullText, sectionWidth);

        lines.forEach((line, index) => {
          if (index === 0 && suffix && bold) {
            const labelWidth = doc.getTextWidth(text);
            doc.setFont("Inter-Bold", "bold");
            doc.text(text, startX, y);
            doc.setFont("Inter-Regular", "normal");
            doc.text(suffix, startX + labelWidth, y);
          } else {
            doc.setFont(
              bold ? "Inter-Bold" : "Inter-Regular",
              bold ? "bold" : "normal",
            );
            doc.text(line, startX, y);
          }
          y += lineHeight;
        });
      });

      return y;
    };
    if (hasSaleToData) {
      fromY = renderDetails(fromDetails, padding, fromY);
    }
    toY = renderDetails(toDetails, midX, toY);

    // 3. Product Table
    const productBody = globalData?.saleitems?.map((item, i) => {
      const qty = item.quantity || 1;
      const hsn = item.hsnsac;
      const mrp = item.mrp;
      const gst = `${item.taxpercentage}%`;
      const landingCost=item.lineTotalLandingCost
      const rate = item.soldprice;
      const discount = `₹${
        item.discount }`;
      const total = 
        item.lineTotalSoldPrice ;
      const details = [
        item.hsnsac ? `HSN/SAC: ${item.hsnsac}` : "",
        item.discount > 0
          ? `MRP: ₹${
              item.mrp } Discount: ₹${item.discount}`
          : "",
        item.taxpercentage > 0
          ? `Tax: ₹${item.baseprice} @${
              item.taxpercentage
            }% = ₹${item.taxamount}`
          : "",
      ]
        .filter(Boolean) // removes empty values
        .join(" "); // separator without commas

      const desc = [
        `${item.title}`,
        [
          item.hsnsac ? `HSN/SAC: ${item.hsnsac}` : null,
          item.discount > 0
            ? `MRP: ₹${
                item.mrp }  Discount:₹${
                item.discount
              }`
            : null,
          item.taxpercentage > 0
            ? `Tax:₹${item.baseprice} @${
                item.taxpercentage
              }% = ₹${item.taxamount}`
            : null,
        ],
      ].join("\n");

      return [
        i + 1,
        `${item.title}`,
        hsn,
        `₹${mrp}`,
        gst,
        `₹${rate}`,
        `₹${qty} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        discount,
        `₹${total}`,
      ];
    });
    // Add total row INSIDE the same table
    productBody.push([
      {
        content: "",
        colSpan: 4,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: "Total",
        colSpan: 2,
        styles: { halign: "right", fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: "",
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `₹${
          globalData.totaldiscount }`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `₹${
          globalData.grandtotal }`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
    ]);
    autoTable(doc, {
      startY: Math.max(fromY, toY),
      head: [
        [
          "S.No",
          "Items",
          "HSN/SAC",
          "MRP",
          "GST",
          "Rate",
          "Qty",
          "Disc",
          "Total",
        ],
      ],
      body: productBody,
      margin: { left: 6, right: 5 }, // add right margin if needed
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 7,
        halign: "middle",
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#041121",
      },
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        fontStyle: "bold",
        font: "Inter-Bold",
        halign: "center",
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 7, halign: "center", valign: "middle" },
        1: { cellWidth: 80, halign: "left" },
        2: { cellWidth: 17, halign: "center", valign: "middle" },
        3: { cellWidth: 18, halign: "center", valign: "middle" },
        4: { cellWidth: 10, halign: "center", valign: "middle" },
        5: { cellWidth: 18, halign: "center", valign: "middle" },
        6: { cellWidth: 10, halign: "center", valign: "middle" },
        7: { cellWidth: 18, halign: "center", valign: "middle" },
        8: { cellWidth: 18, halign: "center", valign: "middle" },
      },
      willDrawCell: function (data) {
        if (data.column.index === 1 && data.section === "body") {
          const itemIndex = data.row.index;
          const item = data?.saleitems?.[itemIndex];
          if (!item) return;

          const x = data.cell.x + 2;
          let y = data.cell.y + 4;
          const lineHeight = 4;
          const currency = CurrencyConverter;

          const lines = [
            { type: "title", text: item.title },
            ...(item.imode === "PRODUCT" && item.note
              ? [{ type: "description", text: item.note }]
              : []),
            ...(item.imode === "SERVICE" && item.description
              ? [{ type: "description", text: item.description }]
              : []),
            ...(item.hasnsac
              ? [
                  {
                    label: item.imode === "SERVICE" ? "SAC:" : "HSN:",
                    value: item.hasnsac,
                  },
                ]
              : []),
            {
              label: "MRP:",
              value: `₹${item.mrp}`,
            },
            {
              label: "Discount:",
              value: `₹${item.discount}`,
            },
          ];

          data.cell.text = [];
          lines.forEach((line) => {
            if (line.type === "title") {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(15);
              doc.setTextColor(0, 0, 0);
              doc.text(line.text, x, y);
              doc.setFontSize(9);
            } else {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(9);
              doc.setTextColor(0, 0, 0);
              doc.text(line.label, x, y);
              doc.setFont("Inter-Regular", "normal");
              doc.setTextColor(80, 80, 80);
              doc.text(line.value, x + 32, y);
            }
            y += lineHeight;
          });
        }
      },

      theme: "grid",
    });
    const isInterState =
      settings?.saleProfileData?.statecode &&
      globalData.saleto?.statecode &&
      globalData.salefrom.statecode !== globalData.saleto.statecode;
    const body = [
      [
        {
          content: "Total Amount In Words",
          styles: {
            fontStyle: "bold",
            font: "Inter-Bold",
            fontSize: 8,
            valign: "top",
          },
        },
        {
          content: "Grand Total",
          styles: { halign: "right", fontSize: 8 },
        },
        {
          content: `₹${
            globalData.grandtotal}`,
          styles: { halign: "center", valign: "middle", fontSize: 8 },
        },
      ],
      [
        {
          content: globalData.amountinwords,
          rowSpan: isInterState ? 3 : 4,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: 8,
          },
        },
        {
          content: "Grand Total (Rounded)",
          styles: { fontStyle: "bold", halign: "right", fontSize: 8 },
        },
        {
          content: `₹ ${globalData.grandtotal }`,
          styles: {
            halign: "center",
            valign: "middle",
            fontStyle: "bold",
            fontSize: 8.5,
          },
        },
      ],
    ];
    const afterProductsY = doc.lastAutoTable?.finalY + 0.4;
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginBottom = 20;
    const estimatedSummaryHeight = 25;
    let summaryStartY;
    let isSamePage = true;
    if (afterProductsY + estimatedSummaryHeight + marginBottom <= pageHeight) {
      summaryStartY = afterProductsY;
    } else {
      doc.addPage();
      summaryStartY = 20;
      isSamePage = false;
    }
    autoTable(doc, {
      startY: summaryStartY,
      body: body,
      pageBreak: "avoid",
      margin: { left: 6, right: 5 },
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        halign: "middle",
        cellPadding: 2,
        fontSize: 9,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme.templateTheme?.foregroundColor,
      },
      columnStyles: {
        0: { cellWidth: 122 },
        1: { cellWidth: 56, halign: "right" },
        2: { cellWidth: 18, halign: "center", valign: "middle" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && isSamePage) {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
        const lastRowIndex = body.length - 1;
        if (data.section === "body") {
          if (
            data.row.index === lastRowIndex &&
            (data.column.index === 1 || data.column.index === 2)
          ) {
            // data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.font = "Inter-Bold";
            data.cell.styles.fontStyle = "bold";
          } else {
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fillColor = "#ffffff";
            data.cell.styles.font = "Inter-Regular";
            data.cell.styles.fontStyle = "normal";
          }
        }
      },
      theme: "grid",
    });
    const afterSummaryY = doc.lastAutoTable.finalY;
    const pageHeight2 = doc.internal.pageSize.getHeight();
    const marginBottom2 = 20;
    const declarationHeight = 45;
    let finalY;
    if (afterSummaryY + declarationHeight + marginBottom2 > pageHeight2) {
      doc.addPage();
      finalY = 10;
    } else {
      finalY = afterSummaryY;
    }
    let currentY = finalY + 10;

    if ((globalData?.totaldiscount) > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text(
        `Your Total Discount : ₹${globalData?.totaldiscount }`,
        105,
        currentY,
        { align: "center" }
      );
    
      currentY += 2; // ONLY if discount exists
    }

     const paymentRows = [];
    
    const cashPaid =
    globalData.cashPaid;
    
    const cardPaid =
    globalData.cardPaid;
    
    const onlinePaid =
    globalData.onlinePaid;
    
    const upiPaid =
    globalData.upiPaid;
    
    const balanceDue =
    globalData.balanceDue;
      
    // condition based rows
    if (cashPaid > 0) {
      paymentRows.push(["Cash", `₹${cashPaid}`]);
    }
    
    if (cardPaid > 0) {
      paymentRows.push(["Card", `₹${cardPaid}`]);
    }
    
    if (onlinePaid > 0) {
      paymentRows.push(["Online Amount", `₹${onlinePaid}`]);
    }
    
    if (upiPaid > 0) {
      paymentRows.push(["UPI", `₹${upiPaid}`]);
    }
    
    if (balanceDue > 0) {
      paymentRows.push([
        "Balance Due",
        `₹${balanceDue}`,
      ]);
    }
    
    // draw table ONLY if rows exist
    if (paymentRows?.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [["Payment Mode", ""]],
        body: paymentRows,
        margin: { left: 6 },
        tableWidth: 70,
        styles: {
          font: "Inter-Regular",
          fontSize: 8,
          cellPadding: 2,
          lineColor: selectedTheme?.templateTheme?.borderColor,
          lineWidth: 0.2,
          textColor: "#000",
        },
        headStyles: {
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          textColor: selectedTheme?.templateTheme?.foregroundColor,
          font: "Inter-Bold",
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: 30, halign: "right" },
        },
        theme: "grid",
      });
    
      currentY = doc.lastAutoTable.finalY + 6;
    }
    
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(
      "***** Thank You! Visit Us Again *****",
      pageWidth / 2,
      currentY,
      { align: "center" }
    );
    currentY += 5;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(
      "Powerd By",
      pageWidth / 2,
      currentY,
      { align: "center" }
    );
    if(storeSettings?.negobillBranding){
    currentY += 5;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(
      "www.NegoBill.com",
      pageWidth / 2,
      currentY,
      { align: "center" }
    );
    }
    currentY += 8;

    const contentWidth = pageWidth - padding * 2;

    // 70% for Terms
    const termsWidth = contentWidth * 0.7;

    // 30% for Signature
    const signWidth = contentWidth * 0.3;

    const leftX = padding;

    const startY = currentY;
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Terms & Conditions", leftX, startY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      const splitTerms = doc.splitTextToSize(terms, termsWidth - 5);
      doc.text(splitTerms, leftX, startY + 4);
    }

    const boxTopY = currentY + 3;
    // const boxTopY = footerY + 7;
    const boxHeight = 20;
    const boxLeftX = padding;
    const boxRightX = pageWidth - padding;
    const boxWidth = boxRightX - boxLeftX;
    const boxMidX = boxLeftX + boxWidth / 2;

    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const titleWidths = doc.getTextWidth("Issued By,Signature");
    doc.text(
      "Issued By,Signature",
      addressX + maxTitleWidth - titleWidths,
      boxTopY,
    );
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.text(titleText, addressX + maxTitleWidth - titleWidth - 6, boxTopY + 4);
    const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;
    if (storeSettings?.invoiceSettings?.signature) {
      try {
        // Convert image URL to base64
        const response = await fetch(signimg);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Load original dimensions
        const { width: naturalWidth, height: naturalHeight } =
          await loadImageDimensions(base64);

        // Resize logic
        const maxWidth = 50;
        const maxHeight = 10;
        let imgWidth = naturalWidth;
        let imgHeight = naturalHeight;
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        imgWidth *= ratio;
        imgHeight *= ratio;

        const getImageFormat = (path) => {
          const ext = path.split(".").pop().toLowerCase();
          if (ext === "jpg" || ext === "jpeg") return "JPEG";
          if (ext === "png") return "PNG";
          return "JPEG";
        };
        const format = getImageFormat(signimg);

        doc.addImage(
          base64,
          format,
          addressX + maxTitleWidth - titleWidths - 15,
          boxTopY + 6,
          imgWidth,
          imgHeight,
        );
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    setDownload(false);
    setPrint(false);
    return doc;
  };
  const handleDownload = async (data) => {
    setDownload(true);
    const { salemode, saleno } = data || {};

    let doc;
    if (salemode?.toUpperCase() === "BILL") {
      doc = await generateBillingPDF();
    } else {
      doc = await generateinvoicePDF();
    }

    const filenamePrefix = formatTrackMode(selectedSize)?.replace(/\s+/g, "_");
    doc.save(`${filenamePrefix}_${saleno}.pdf`);
  };

  const handlePrint = async (data, overrideSize = null) => {
    setPrint(true);
    try {
      if (!checkNetwork()) return;

      const paperSize = overrideSize || selectedSize;
      const upperMode = data?.salemode?.toUpperCase();
      let doc;

      if (paperSize === "BILL") {
        doc = await generateBillingPDF();
      } else {
        doc = await generateinvoicePDF();
      }

      const blob = doc.output("blob");
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 500);
      };
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPrint(false);
    }
  };

  useEffect(() => {
    jsPDF.API.events.push([
      "addFonts",
      function () {
        this.addFileToVFS("Inter_24pt-Regular-normal.ttf", InterRegular);
        this.addFileToVFS("Inter_24pt-Bold-normal.ttf", InterBold);
        this.addFont(
          "Inter_24pt-Regular-normal.ttf",
          "Inter-Regular",
          "normal",
        );
        this.addFont("Inter_24pt-Bold-normal.ttf", "Inter-Bold", "bold");
      },
    ]);
  }, []);

  useEffect(() => {
    setData(isViewData);
  }, [isViewData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "p") {
        event.preventDefault();
        handlePrint(data);
      }
    };
    const handleAfterPrint = () => {
      setTimeout(() => {
        window.focus();
      }, 100);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [data]);
  return (
    <>
    <div className="invoice-scale">
      {selectedSize === "INVOICE" ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in">
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="40%" style={{ textAlign: "left" }}>
                        <div className="tm_logo">
                          {("/assets/images/logo-light.png" ||
                            settings?.saleProfileData?.image) && (
                            <img
                              src={
                                settings?.saleProfileData?.image
                                  ? uRLs.IMAGE_URL +
                                    "profile/" +
                                    settings?.saleProfileData?.image
                                  : "/assets/images/logo-light.png"
                              }
                              className="img-rounded logo mb-0"
                              height="50"
                            />
                          )}
                        </div>
                      </td>
                      <td width="60%" style={{ textAlign: "right" }}>
                        <div className="tm_primary_color fs-5 tm_text_uppercase">
                          {globalData?.salemode && formatTrackMode(globalData?.salemode)}
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              marginBottom: "0px",
                            }}
                          >
                            {settings?.saleProfileData?.title || ""}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              lineHeight: "18px",
                              marginBottom: "0px",
                            }}
                          >
                            <div>
                              {settings?.saleProfileData?.addressline1 ||
                                ""}{" "}
                            </div>
                            <div>
                              {settings?.saleProfileData?.addressline2 ||
                                ""}{" "}
                            </div>
                            <div>
                              {settings?.saleProfileData?.district || ""}{" "}
                              {settings?.saleProfileData?.state || ""}{" "}
                              {settings?.saleProfileData?.country || ""} -{" "}
                              {settings?.saleProfileData?.pincode || ""}
                              Phone:+91{" "}
                              {settings?.saleProfileData?.phone || "-"}{" "}
                            </div>
                            <div>
                              Email: {settings?.saleProfileData?.email || "-"}
                            </div>
                            {/* <div>
                           <strong>GST:</strong>{" "}
                           {settings?.saleProfileData?.gstnumber || "-"}{" "}
                           <strong>PAN:</strong>{" "}
                           {settings?.saleProfileData?.pannumber || "-"}
                         </div> */}
                            {settings?.saleProfileData?.fssai && (
                              <div>
                                FSSAI: {settings?.saleProfileData?.fssai}
                              </div>
                            )}
                            <div>
                              State: {settings?.saleProfileData?.state || ""} [
                              State Code:{" "}
                              {settings?.saleProfileData?.statecode || ""} ]
                            </div>
                          </div>
                        </div>
                        {globalData?.status === "CANCELLED" && (
                          <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                            {globalData?.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="tm_invoice_info tm_mb20">
                  <div
                    className="tm_invoice_seperator tm_gray_bg"
                    style={{
                      backgroundColor:
                        selectedTheme?.templateTheme?.primaryColor,
                    }}
                  ></div>
                  <div className="tm_invoice_info_list">
                    <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                      <b className="tm_primary_color">Invoice No: </b>{" "}
                      {globalData?.saleno}
                    </p>
                    <p className="tm_invoice_date tm_m0 mt-3">
                      <b className="tm_primary_color"> Date: </b>{" "}
                      {saleTime?.date}
                    </p>
                  </div>
                </div>
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="45%" style={{ verticalAlign: "top" }}>
                        {globalData?.saleto &&
                          (globalData?.saleto.name ||
                            globalData?.saleto.addressline1 ||
                            globalData?.saleto.addressline2 ||
                            globalData?.saleto.state ||
                            globalData?.saleto.district ||
                            globalData?.saleto.country ||
                            globalData?.saleto.pincode ||
                            globalData?.saleto.phone ||
                            globalData?.saleto.email ||
                            globalData?.saleto.gstnumber ||
                            globalData?.saleto.statecode ||
                            globalData?.saleto.referid) && (
                            <div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                Invoice To:
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                {globalData?.saleto?.name || ""}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  lineHeight: "18px",
                                  marginBottom: "0px",
                                }}
                              >
                                <div>
                                  {globalData?.saleto?.addressline1 || ""}{" "}
                                  {/* {globalData?.saleto?.addressline2 || ""}{" "} */}
                                  {globalData?.saleto?.state || ""}{" "}
                                  {globalData?.saleto?.district || ""}{" "}
                                  {globalData?.saleto?.country || ""} -{" "}
                                  {globalData?.saleto?.pincode || ""}
                                </div>
                                {globalData?.saleto?.phone && (
                                  <div>Phone: +91 {globalData?.saleto?.phone}</div>
                                )}
                                {globalData?.saleto?.email && (
                                  <div>Email: {globalData?.saleto?.email}</div>
                                )}
                                {globalData?.saleto?.gstnumber && (
                                  <div>GST: {globalData?.saleto?.gstnumber}</div>
                                )}
                                {globalData?.saleto?.state && (
                                  <div>
                                    State: {globalData?.saleto?.state} [ State Code :
                                    {globalData?.saleto?.statecode || ""} ]
                                  </div>
                                )}
                                {globalData?.saleto?.referid && (
                                  <div>
                                    Reference No: {globalData?.saleto?.referid}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </td>

                      <td width="10%"></td>

                      <td width="45%" style={{ verticalAlign: "top" }}>
                         {globalData.saleto?.shippingAddress &&
                           (globalData.saleto?.shippingAddress?.addressline1 ||
                            globalData.saleto?.shippingAddress?.addressline2 ) && (
                            <div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                Shipping Address:
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                {globalData?.saleto?.name || ""}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  lineHeight: "18px",
                                  marginBottom: "0px",
                                }}
                              >
                                <div>
                                  {globalData?.saleto?.shippingAddress?.addressline1 || ""}{" "}
                                  {globalData?.saleto?.shippingAddress?.addressline2 || ""}{" "}
                                  {globalData?.saleto?.shippingAddress?.state || ""}{" "}
                                  {globalData?.saleto?.shippingAddress?.district || ""}{" "}
                                  {globalData?.saleto?.shippingAddress?.country || ""} -{" "}
                                  {globalData?.saleto?.shippingAddress?.pincode || ""}
                                </div>
                                {globalData?.saleto?.shippingAddress?.phone && (
                                  <div>Phone: +91 {globalData?.saleto?.shippingAddress?.phone}</div>
                                )}
                                {globalData?.saleto?.shippingAddress?.email && (
                                  <div>Email: {globalData?.saleto?.shippingAddress?.email}</div>
                                )}
                              </div>
                            </div>
                          )}
                        {/* <div>
                       <p className="tm_invoice_number tm_m0 mb-0 mt-1">
                         <b className="tm_primary_color">
                           Bill No:{" "}
                         </b>{" "}
                         {globalData?.saleno}
                       </p>
                     </div> */}
                        {/* <div>
                           <p className="tm_invoice_date tm_m0 mt-1">
                           <b className="tm_primary_color">
                               {" "}
                               Date:{" "}
                           </b>{" "}
                           {saleTime.date}
                           </p>
                       </div> */}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table
                  // className="items"
                  width="100%"
                  style={{
                    marginTop: "6px",
                    borderCollapse: "collapse",
                  }}
                  cellPadding="4"
                >
                  <thead>
                    <tr>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        S.No
                      </th>
                      <th
                        className="force-style"
                        style={{
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Items
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        HSN/SAC
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        MRP
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        GST
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Rate
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Qty
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Disc
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalData?.saleitems?.map((product, index) => (
                      <tr key={index}>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "5%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "left",
                            fontSize: "12px",
                            width: "65%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div>{product.title}</div>
                          {/* {product.imode === "SERVICE" && (
                         <div className="ms-0 fsz-10 lh-13 mb-1">
                           {product.description}
                         </div>
                       )}
                       {product.imode === "PRODUCT" &&
                         product.note && (
                           <div className="ms-0 fs-12 text-truncate-description text-muted">
                             {product?.note}
                           </div>
                         )} */}

                          {/* <div>
                         {product.hsnsac && (
                           <span>
                             <span className="fw-medium">
                               HSN/SAC :{" "}
                             </span>{" "}
                             {product.hsnsac}
                           </span>
                         )}
                         {product.discount > 0 && (
                           <>
                             <>
                               {" "}
                               <span className="fw-medium">
                                 MRP :{" "}
                               </span>{" "}
                               ₹
                               {CurrencyConverter.toCurrency(
                                 product.mrp || 0,
                                 "INR"
                               )}
                             </>{" "}
                             <>
                               <span className="fw-medium">
                                 Discount :{" "}
                               </span>{" "}
                                ₹
                               {CurrencyConverter.toCurrency(
                                 product.discount || 0,
                                 "INR"
                               )}
                             </>
                           </>
                         )}{" "}
                         {product.taxpercentage > 0 && (
                           <>
                             <span className="fw-medium">
                               Tax :{" "}
                             </span>{" "}
                             ₹
                             {CurrencyConverter.toCurrency(
                               product.baseprice || 0,
                               "INR"
                             )}
                             @{product.taxpercentage}% = ₹
                             {CurrencyConverter.toCurrency(
                               product.taxamount || 0,
                               "INR"
                             )}
                           </>
                         )}
                       </div> */}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product?.hsnsac}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product?.mrp }
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "7%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product?.taxpercentage}%
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product?.soldprice}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product?.quantity} {(() => {
                              const unitCode = product?.isSecondaryUnit
                                ? product?.unitConfig?.secondaryUnitCode
                                : product?.unitConfig?.primaryUnitCode;
                              return unitCode || "";
                            })()}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product?.discount }
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product?.lineTotalSoldPrice }
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        colSpan={4}
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan={2}
                      >
                        Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {globalData?.totaldiscount > 0
                          ? `₹${globalData?.totaldiscount }`
                          : "-"}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.totalsoldprice}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="4"
                      >
                        Total Amount In Words
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="4"
                      >
                        Grand Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.grandtotal}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          // "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          // "--th-color": selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="4"
                        rowSpan="5"
                      >
                        {globalData?.amountinwords}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="4"
                      >
                        Grand Total (Rounded)
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.grandtotal }
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table width="100%">
                  <tbody>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          textAlign: "center",
                          padding: "8px",
                        }}
                      >
                        Your Total Discount : ₹
                        {globalData?.totaldiscount }
                        {/* <br />
                        <div className="mt-1 fs-20">
                          <span>***** Thank You! Visit Us Again *****</span>
                        </div> */}
                      </td>
                    </tr>
                  </tbody>
                </table>
                { globalData?.grandtotal >0 &&(
                            <table
                            className="items"
                            width="38%"
                            style={{
                              marginTop: "4px",
                              // borderCollapse: "collapse",
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr>
                                <th
                                  colSpan={2}
                                  className="force-style"
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                                      "--th-color": selectedTheme?.templateTheme?.foregroundColor,
                                  }}
                                >
                                  Payment Mode
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {globalData.cashPaid>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Cash</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.cashPaid}
                                </td>
                              </tr>)}
                              {globalData.cardPaid>0&&(
                              <tr>
                                <td  className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Card</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.cardPaid}
                                </td>
                              </tr>)}
                              {globalData.onlinePaid>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>OnlineAmount</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.onlinePaid}
                                </td>
                              </tr>)}
                              {globalData.upiPaid>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>UPI</td>
                                <td
                                 className="force-style"
                                 style={{
                                   textAlign: "start",
                                   fontSize: "12px",
                                   width: "10%",
                                   fontWeight:"600",
                                   "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                 }}
                                >
                                  ₹{globalData.upiPaid}
                                </td>
                              </tr>)}
                              {globalData.balanceDue>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>BalanceDue</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.balanceDue}
                                </td>
                              </tr>)}
                            </tbody>
                          </table>
                            )}
                              <div className="mt-2 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        ***** Thank You! Visit Us Again *****
                                      </span>
                                    </div>
                                    {storeSettings?.negobillBranding && (
                                      <>
                                    <div className="mt-1 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        Powered By
                                      </span>
                                    </div>
                                    <div className=" fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        www.NegoBill.com
                                      </span>
                                    </div>
                                    </>)}
                                    <table width="100%">
                                <tbody>
                                  <tr>
                                  {terms?.length > 0 && (
                                    <td
                                      style={{
                                        padding: "8px",
                                      }}
                                      width="70%"
                                    >
                                      <div
                                        className="fs-13 fw-600"
                                        style={{
                                          textAlign: "left",
                                          color: "black",
                                        }}
                                      >
                                        Terms & Conditions
                                      </div>
                                      <div className="terms-box">{terms}</div>
                                    </td>
                                      )}
                                    <td
                                      style={{
                                        padding: "8px",
                                        marginTop: "15px",
                                      }}
                                      className="d-flex align-items-start justify-content-end"
                                    >
                                      <div>
                                        <div className="text-end p-0">
                                          Issued By,Signature
                                        </div>
                                        <div className="text-end p-0 fw-bold ">
                                          {data?.salefrom?.title}
                                        </div>
                                        {storeSettings?.invoiceSettings
                                          ?.signature && (
                                          <div className="text-end p-0 fw-bold ">
                                            <img
                                              src={
                                                uRLs.IMAGE_URL +
                                                storeSettings?.invoiceSettings
                                                  ?.signature
                                              }
                                              width={150}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                {/* <table
                 className="items"
                 width="100%"
                 style={{ borderCollapse: "collapse" }}
                 cellPadding="8"
               >
                 <tbody>
                   <tr>
                     <td
                       style={{
                         verticalAlign: "middle",
                         width: "52.5%",
                         border: "0.5mm solid #e0e0e0",
                         textAlign: "left",
                         padding: "8px",
                       }}
                     >
                       <span
                         style={{
                           fontSize: "12px",
                           fontWeight: 600,
                         }}
                       >
                         Declaration:
                       </span>
                       <br />
                       <span style={{ fontSize: "12px" }}>
                         We declare that this bill recipet shows
                         the actual price of the goods described
                         and that all particulars are true and
                         correct.
                       </span>
                     </td>
                     <td
                       style={{
                         width: "50%",
                         fontSize: "12px",
                         fontWeight: "500",
                         border: "0.5mm solid #e0e0e0",
                         textAlign: "center",
                         padding: "8px",
                       }}
                     >
                       * Computer Generated Document No sign
                       Required
                     </td>
                   </tr>
                 </tbody>
               </table> */}
              </div>
            </div>
          </div>
        </div>
      ) : selectedSize === "BILL" ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in">
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="40%" style={{ textAlign: "left" }}>
                        <div className="tm_logo">
                          {(settings?.saleProfileData?.image ||
                            "/assets/images/logo-light.png") && (
                            <img
                              src={
                                settings?.saleProfileData?.image
                                  ? uRLs.IMAGE_URL +
                                    "profile/" +
                                    settings?.saleProfileData?.image
                                  : "/assets/images/logo-light.png"
                              }
                              className="img-rounded logo mb-0"
                              height="50"
                            />
                          )}
                        </div>
                      </td>
                      <td width="60%" style={{ textAlign: "right" }}>
                        <div className="tm_primary_color fs-5 tm_text_uppercase">
                          {selectedSize && formatTrackMode(selectedSize)}
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              marginBottom: "0px",
                            }}
                          >
                            {settings?.saleProfileData?.title || ""}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              lineHeight: "18px",
                              marginBottom: "0px",
                            }}
                          >
                            <div>
                              {settings?.saleProfileData?.addressline1 ||
                                ""}{" "}
                            </div>
                            <div>
                              {settings?.saleProfileData?.addressline2 ||
                                ""}{" "}
                            </div>
                            <div>
                              {settings?.saleProfileData?.district || ""}{" "}
                              {settings?.saleProfileData?.state || ""}{" "}
                              {settings?.saleProfileData?.country || ""} -{" "}
                              {settings?.saleProfileData?.pincode || ""}
                              Phone:+91{" "}
                              {settings?.saleProfileData?.phone || "-"}{" "}
                            </div>
                            <div>
                              Email: {settings?.saleProfileData?.email || "-"}
                            </div>
                            {/* <div>
                            <strong>GST:</strong>{" "}
                            {settings?.saleProfileData?.gstnumber || "-"}{" "}
                            <strong>PAN:</strong>{" "}
                            {settings?.saleProfileData?.pannumber || "-"}
                          </div> */}
                            {settings?.saleProfileData?.fssai && (
                              <div>
                                FSSAI: {settings?.saleProfileData?.fssai}
                              </div>
                            )}
                            <div>
                              State: {settings?.saleProfileData?.state || ""} [
                              State Code:{" "}
                              {settings?.saleProfileData?.statecode || ""} ]
                            </div>
                          </div>
                        </div>
                        {data?.status === "CANCELLED" && (
                          <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                            {data?.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="tm_invoice_info tm_mb20">
                  <div
                    className="tm_invoice_seperator tm_gray_bg"
                    style={{
                      backgroundColor:
                        selectedTheme?.templateTheme?.primaryColor,
                    }}
                  ></div>
                  <div className="tm_invoice_info_list">
                    <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                      <b className="tm_primary_color">Bill No: </b>{" "}
                      {data?.saleno}
                    </p>
                    <p className="tm_invoice_date tm_m0 mt-3">
                      <b className="tm_primary_color"> Date: </b>{" "}
                      {saleTime.date}
                    </p>
                  </div>
                </div>
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="45%" style={{ verticalAlign: "top" }}>
                        {data?.saleto &&
                          (data?.saleto.name ||
                            data?.saleto.addressline1 ||
                            data?.saleto.addressline2 ||
                            data?.saleto.state ||
                            data?.saleto.district ||
                            data?.saleto.country ||
                            data?.saleto.pincode ||
                            data?.saleto.phone ||
                            data?.saleto.email ||
                            data?.saleto.gstnumber ||
                            data?.saleto.statecode ||
                            data?.saleto.referid) && (
                            <div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                Bill To:
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                {data?.saleto?.name || ""}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  lineHeight: "18px",
                                  marginBottom: "0px",
                                }}
                              >
                                <div>
                                  {data?.saleto?.addressline1 || ""}{" "}
                                  {/* {data?.saleto?.addressline2 || ""}{" "} */}
                                  {data?.saleto?.state || ""}{" "}
                                  {data?.saleto?.district || ""}{" "}
                                  {data?.saleto?.country || ""} -{" "}
                                  {data?.saleto?.pincode || ""}
                                </div>
                                {data?.saleto?.phone && (
                                  <div>Phone: +91 {data?.saleto?.phone}</div>
                                )}
                                {data?.saleto?.email && (
                                  <div>Email: {data?.saleto?.email}</div>
                                )}
                                {data?.saleto?.gstnumber && (
                                  <div>GST: {data?.saleto?.gstnumber}</div>
                                )}
                                {data?.saleto?.state && (
                                  <div>
                                    State: {data?.saleto?.state} [ State Code :
                                    {data?.saleto?.statecode || ""} ]
                                  </div>
                                )}
                                {data?.saleto?.referid && (
                                  <div>
                                    Reference No: {data?.saleto?.referid}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </td>

                      <td width="10%"></td>

                      <td width="45%" style={{ verticalAlign: "top" }}>
                        {data?.saleto &&
                          (data?.saleto.name ||
                            data?.saleto.addressline1 ||
                            data?.saleto.addressline2 ||
                            data?.saleto.state ||
                            data?.saleto.district ||
                            data?.saleto.country ||
                            data?.saleto.pincode ||
                            data?.saleto.phone ||
                            data?.saleto.email ||
                            data?.saleto.gstnumber ||
                            data?.saleto.statecode ||
                            data?.saleto.referid) && (
                            <div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                Shipping Address:
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                {data?.saleto?.name || ""}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  lineHeight: "18px",
                                  marginBottom: "0px",
                                }}
                              >
                                <div>
                                  {data?.saleto?.addressline1 || ""}{" "}
                                  {/* {data?.saleto?.addressline2 || ""}{" "} */}
                                  {data?.saleto?.state || ""}{" "}
                                  {data?.saleto?.district || ""}{" "}
                                  {data?.saleto?.country || ""} -{" "}
                                  {data?.saleto?.pincode || ""}
                                </div>
                                {data?.saleto?.phone && (
                                  <div>Phone: +91 {data?.saleto?.phone}</div>
                                )}
                                {data?.saleto?.email && (
                                  <div>Email: {data?.saleto?.email}</div>
                                )}
                              </div>
                            </div>
                          )}
                        {/* <div>
                        <p className="tm_invoice_number tm_m0 mb-0 mt-1">
                          <b className="tm_primary_color">
                            Bill No:{" "}
                          </b>{" "}
                          {data?.saleno}
                        </p>
                      </div> */}
                        {/* <div>
                            <p className="tm_invoice_date tm_m0 mt-1">
                            <b className="tm_primary_color">
                                {" "}
                                Date:{" "}
                            </b>{" "}
                            {saleTime.date}
                            </p>
                        </div> */}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table
                  // className="items"
                  width="100%"
                  style={{
                    marginTop: "6px",
                    borderCollapse: "collapse",
                  }}
                  cellPadding="4"
                >
                  <thead>
                    <tr>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          // border: "0.5mm solid #e0e0e0",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        S.No
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Items
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Rate
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          border: `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Qty
                      </th>
                      <th
                        className="force-style"
                        style={{
                          // width: "10px",
                          textAlign: "center",
                          //   background: "#f3f3f3",
                          border: `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.saleitems?.map((product, index) => (
                      <tr key={index}>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "5%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "left",
                            fontSize: "12px",
                            width: "65%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div>{product.title}</div>
                          {/* {product.imode === "SERVICE" && (
                          <div className="ms-0 fsz-10 lh-13 mb-1">
                            {product.description}
                          </div>
                        )}
                        {product.imode === "PRODUCT" &&
                          product.note && (
                            <div className="ms-0 fs-12 text-truncate-description text-muted">
                              {product?.note}
                            </div>
                          )} */}

                          {/* <div>
                          {product.hsnsac && (
                            <span>
                              <span className="fw-medium">
                                HSN/SAC :{" "}
                              </span>{" "}
                              {product.hsnsac}
                            </span>
                          )}
                          {product.discount > 0 && (
                            <>
                              <>
                                {" "}
                                <span className="fw-medium">
                                  MRP :{" "}
                                </span>{" "}
                                ₹
                                {CurrencyConverter.toCurrency(
                                  product.mrp || 0,
                                  "INR"
                                )}
                              </>{" "}
                              <>
                                <span className="fw-medium">
                                  Discount :{" "}
                                </span>{" "}
                                 ₹
                                {CurrencyConverter.toCurrency(
                                  product.discount || 0,
                                  "INR"
                                )}
                              </>
                            </>
                          )}{" "}
                          {product.taxpercentage > 0 && (
                            <>
                              <span className="fw-medium">
                                Tax :{" "}
                              </span>{" "}
                              ₹
                              {CurrencyConverter.toCurrency(
                                product.baseprice || 0,
                                "INR"
                              )}
                              @{product.taxpercentage}% = ₹
                              {CurrencyConverter.toCurrency(
                                product.taxamount || 0,
                                "INR"
                              )}
                            </>
                          )}
                        </div> */}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹
                          {CurrencyConverter.toCurrency(
                            product.soldprice || 0,
                            "INR",
                          )}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.quantity}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            width: "10%",
                            border: "var(--td-border)",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹
                          {CurrencyConverter.toCurrency(
                            product.lineTotalSoldPrice || 0,
                            "INR",
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        colSpan={2}
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {data?.totalqty}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹
                        {CurrencyConverter.toCurrency(
                          data?.totalsoldprice || 0,
                          "INR",
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Total Amount In Words
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Grand Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹
                        {CurrencyConverter.toCurrency(
                          data?.grandtotal || 0,
                          "INR",
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          // color: "var(--th-color)",
                          // backgroundColor: "var(--th-bg)",
                          border: "var(--td-border)",
                          // "--th-color": selectedTheme?.templateTheme?.foregroundColor,
                          // "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                        rowSpan="5"
                      >
                        {data?.amountinwords}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "var(--th-color)",
                          backgroundColor: "var(--th-bg)",
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Grand Total (Rounded)
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "var(--th-color)",
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹
                        {CurrencyConverter.toCurrency(
                          data?.grandtotal || 0,
                          "INR",
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table width="100%">
                  <tbody>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          textAlign: "center",
                          padding: "8px",
                          color: "black",
                          // "--th-color": `${
                          //   selectedTheme.key === "DF"
                          //     ? "#000000"
                          //     : selectedTheme?.templateTheme?.primaryColor
                          // }`,
                          // color: "var(--th-color)",
                        }}
                      >
                        Your Total Discount : ₹
                        {CurrencyConverter.toCurrency(
                          data?.totaldiscount || 0,
                          "INR",
                        )}
                        {/* <br />
                        <div className="mt-1 fs-20">
                          <span>***** Thank You! Visit Us Again *****</span>
                        </div> */}
                      </td>
                    </tr>
                  </tbody>
                </table>
                { data?.grandtotal >0 &&(
                            <table
                            className="items"
                            width="38%"
                            style={{
                              marginTop: "4px",
                              // borderCollapse: "collapse",
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr>
                                <th
                                  colSpan={2}
                                  className="force-style"
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                                      "--th-color": selectedTheme?.templateTheme?.foregroundColor,
                                  }}
                                >
                                  Payment Mode
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {((data.cashamount || 0) - (data.cashchange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Cash</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data.cashamount || 0) - (data.cashchange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.cardamount || 0) - (data.cardchange || 0))>0&&(
                              <tr>
                                <td  className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Card</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data.cardamount || 0) - (data.cardchange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.onlineamount || 0) - (data.onlinechange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>OnlineAmount</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data.onlineamount || 0) - (data.onlinechange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.upiamount || 0) - (data.upichange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>UPI</td>
                                <td
                                 className="force-style"
                                 style={{
                                   textAlign: "start",
                                   fontSize: "12px",
                                   width: "10%",
                                   fontWeight:"600",
                                   "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                 }}
                                >
                                  {formatCurrency(
                            (data.upiamount || 0) - (data.upichange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.paylateramount || 0) - (data.paylaterchange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>BalanceDue</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data.paylateramount || 0) -
                              (data.paylaterchange || 0)
                          )}
                                </td>
                              </tr>)}
                            </tbody>
                          </table>
                            )}
                              <div className="mt-2 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        ***** Thank You! Visit Us Again *****
                                      </span>
                                    </div>
                                    {storeSettings?.negobillBranding && (
                                      <>
                                    <div className="mt-1 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        Powered By
                                      </span>
                                    </div>
                                    <div className=" fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        www.NegoBill.com
                                      </span>
                                    </div>
                                    </>)}
                                    <table width="100%">
                                <tbody>
                                  <tr>
                                  {terms?.length > 0 && (
                                    <td
                                      style={{
                                        padding: "8px",
                                      }}
                                      width="70%"
                                    >
                                      <div
                                        className="fs-13 fw-600"
                                        style={{
                                          textAlign: "left",
                                          color: "black",
                                        }}
                                      >
                                        Terms & Conditions
                                      </div>
                                      <div className="terms-box">{terms}</div>
                                    </td>
                                      )}
                                    <td
                                      style={{
                                        padding: "8px",
                                        marginTop: "15px",
                                      }}
                                      className="d-flex align-items-start justify-content-end"
                                    >
                                      <div>
                                        <div className="text-end p-0">
                                          Issued By,Signature
                                        </div>
                                        <div className="text-end p-0 fw-bold ">
                                          {data?.salefrom?.title}
                                        </div>
                                        {storeSettings?.invoiceSettings
                                          ?.signature && (
                                          <div className="text-end p-0 fw-bold ">
                                            <img
                                              src={
                                                uRLs.IMAGE_URL +
                                                storeSettings?.invoiceSettings
                                                  ?.signature
                                              }
                                              width={150}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                {/* <table
                  className="items"
                  width="100%"
                  style={{ borderCollapse: "collapse" }}
                  cellPadding="8"
                >
                  <tbody>
                    <tr>
                      <td
                        style={{
                          verticalAlign: "middle",
                          width: "52.5%",
                          border: "0.5mm solid #e0e0e0",
                          textAlign: "left",
                          padding: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          Declaration:
                        </span>
                        <br />
                        <span style={{ fontSize: "12px" }}>
                          We declare that this bill recipet shows
                          the actual price of the goods described
                          and that all particulars are true and
                          correct.
                        </span>
                      </td>
                      <td
                        style={{
                          width: "50%",
                          fontSize: "12px",
                          fontWeight: "500",
                          border: "0.5mm solid #e0e0e0",
                          textAlign: "center",
                          padding: "8px",
                        }}
                      >
                        * Computer Generated Document No sign
                        Required
                      </td>
                    </tr>
                  </tbody>
                </table> */}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="default-content">
          <p>No content available for this sale mode.</p>
        </div>
      )}
</div>
<div
        className="modal-footer d-block mt-2"
        style={{
          position: "sticky",
          bottom: -4,
          background: "#fff",
          zIndex: 10,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        {payment && (
        <div className="fw-600 mb-2 font-14">PAYMENT SUMMARY</div>)}
        {payment && (
        <table
          className="items table-bordered mb-3"
          width="100%"
          cellPadding="4"
        >
          <tbody className="text-center">
            <tr>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  cursor: isViewData.cashamount > 0 ? "pointer" : "default",
                  padding: "2px 8px",
                }}
                onClick={(e) => {
                  if (isViewData.cashamount > 0) {
                    cashRef.current.openPopover(e);
                  }
                }}
              >
                <div
                  className={
                    isViewData?.cashamount > 0
                      ? "badge rounded-pill bg-label-moon d-inline-flex align-items-center"
                      : "d-inline-flex align-items-center"
                  }
                  style={{ gap: "4px",fontSize:"12px" }}
                >
                  CASH
                  {isViewData?.cashamount > 0 && <ShowIcon/>}
                </div>
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
              >
                CARD
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
              >
                ONLINE
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
              >
                UPI
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  cursor:
                    isViewData?.paylateramount > 0 ? "pointer" : "default",
                  padding: "2px 8px",
                  fontSize:"12px"
                }}
                onClick={(e) => {
                  if (isViewData?.paylateramount > 0) {
                    paylaterRef.current.openPopover(e);
                  }
                }}
              >
                <div
                  className={
                    isViewData?.paylateramount > 0
                      ? "badge rounded-pill bg-label-fire d-inline-flex align-items-center"
                      : "d-inline-flex align-items-center"
                  }
                  style={{ gap: "4px" }}
                >
                  PAYLATER
                  {isViewData?.paylateramount > 0 && (
                    <ShowIcon iClass={"ms-1"}/>
                  )}
                </div>
              </th>
            </tr>
            <tr>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.cashamount || 0) - (isViewData?.cashchange || 0),
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                ₹
                {CurrencyConverter.toCurrency(
                  (isViewData?.cardamount || 0) - (isViewData?.cardchange || 0),
                  "INR",
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.onlineamount || 0) -
                    (isViewData?.onlinechange || 0),
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.upiamount || 0) - (isViewData?.upichange || 0),
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.paylateramount || 0) -
                    (isViewData?.paylaterchange || 0),
                )}
              </td>
            </tr>
          </tbody>
          <CashButton
            ref={cashRef}
            trackId={isViewData?.boxCashInfo?.trackid}
            trackMode={isViewData?.boxCashInfo?.trackmode}
          />
          <PayLaterButton
            ref={paylaterRef}
            trackId={isViewData?.paylaterInfo?.trackid}
            customerId={isViewData?.paylaterInfo?.customerid}
            trackMode={isViewData?.paylaterInfo?.trackmode}
          />
        </table>)}
        {downloadbtn && (
          <div className="my-2 d-flex gap-2">
            <div className="btn-group text-right align-items-center">
              {print ? (
                <button className="btn bg-label-animal btn-sm pb-0 font-12" disabled>
                  <CircularProgress size={12} className="text-white" />
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handlePrint(data)}
                    className="btn bg-label-animal shadow-none btn-sm font-12"
                    id="printreceiptbtn"
                  >
                    <PrinterIcon iClass={"fs-12"}/> Print
                  </button>
                </>
              )}
            </div>

            {download ? (
              <button
                className="btn btn-warning btn-sm pb-0 text-right"
                disabled
              >
                <CircularProgress size={18} className="text-black" />
              </button>
            ) : (
              <button
                className="btn btn-warning btn-sm text-right"
                id="generatebillpdfbtn"
                onClick={() => handleDownload(data)}
              >
                <DownloadIcon iClass={"fs-15"}/>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default InvoiceThumnail2;
